import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { replaceStaffingRow } from '../../api/client'
import Modal from '../Modal'
import { isoWeekNumber } from './weeks'

/**
 * Hand a stretch of one person's commitment to somebody else.
 *
 * One dialog for both reasons it happens, because the only difference is the
 * window: somebody has left and everything ahead moves, or somebody is away
 * for a few weeks and only those move. Picking the reason picks the window,
 * which is the whole of the distinction.
 */
export default function ReplaceModal({ projectId, row, weeks, members, onClose, onDone }) {
  const { t } = useTranslation(['engineering', 'common'])
  // A row already flagged is almost certainly a departure; anything else is
  // almost certainly cover for a few weeks.
  const [scope, setScope] = useState(row.needs_replacement ? 'all' : 'range')
  const [memberId, setMemberId] = useState('')
  const [fromWeek, setFromWeek] = useState('')
  const [toWeek, setToWeek] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [result, setResult] = useState(null)

  // Only weeks this person actually has hours in are worth offering.
  const committed = row.weeks.filter(w => w.hours > 0)
  const options = committed.length > 0 ? committed : row.weeks
  const available = members.filter(m => m.id !== row.team_member_id)

  const label = (monday) => {
    const week = weeks.find(w => w.monday === monday)
    const d = new Date(monday + 'T00:00:00')
    const dd = `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`
    return week ? `S${isoWeekNumber(week.monday)} · ${dd}` : dd
  }

  const submit = async () => {
    if (!memberId) return
    setBusy(true)
    setError('')
    try {
      const body = { team_member_id: Number(memberId) }
      if (scope === 'range') {
        if (!fromWeek || !toWeek) { setError(t('staffing.replacePickWeeks')); setBusy(false); return }
        body.from_week = fromWeek
        body.to_week = toWeek
      }
      const data = await replaceStaffingRow(projectId, row.id, body)
      setResult(data)
      onDone(data)
    } catch (err) {
      setError(err.response?.data?.detail || t('staffing.replaceFailed'))
    } finally {
      setBusy(false)
    }
  }

  if (result) {
    return (
      <Modal title={t('staffing.replaceTitleModal', { name: row.name })} onClose={onClose}>
        <div className="space-y-4">
          <p className="text-sm text-gray-700">
            {t('staffing.replaceDone', {
              hours: result.moved_hours, weeks: result.weeks_moved,
            })}
          </p>
          {result.trimmed_hours > 0 && (
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              {t('staffing.replaceTrimmed', { hours: result.trimmed_hours })}
            </p>
          )}
          <button onClick={onClose}
            className="w-full bg-gray-900 text-white py-2 rounded-lg text-sm font-medium hover:bg-gray-800">
            {t('common:actions.close', 'Cerrar')}
          </button>
        </div>
      </Modal>
    )
  }

  return (
    <Modal title={t('staffing.replaceTitleModal', { name: row.name })} onClose={onClose}>
      <div className="space-y-4">
        {error && <p className="text-red-600 text-sm bg-red-50 px-3 py-2 rounded-lg">{error}</p>}

        <div className="space-y-2">
          <p className="text-xs font-medium text-gray-600">{t('staffing.replaceReason')}</p>
          {[['all', 'replaceScopeAll'], ['range', 'replaceScopeRange']].map(([value, key]) => (
            <label key={value} className="flex items-start gap-2 text-sm text-gray-700">
              <input type="radio" className="mt-1" checked={scope === value}
                     onChange={() => setScope(value)} />
              <span>
                {t(`staffing.${key}`)}
                <span className="block text-xs text-gray-400">
                  {t(`staffing.${key}Hint`)}
                </span>
              </span>
            </label>
          ))}
        </div>

        {scope === 'range' && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {t('staffing.replaceFrom')}
              </label>
              <select value={fromWeek} onChange={e => setFromWeek(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-2 py-2 text-sm">
                <option value="">—</option>
                {options.map(w => (
                  <option key={w.monday} value={w.monday}>{label(w.monday)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {t('staffing.replaceTo')}
              </label>
              <select value={toWeek} onChange={e => setToWeek(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-2 py-2 text-sm">
                <option value="">—</option>
                {options.filter(w => !fromWeek || w.monday >= fromWeek).map(w => (
                  <option key={w.monday} value={w.monday}>{label(w.monday)}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            {t('staffing.replaceWith')}
          </label>
          <select value={memberId} onChange={e => setMemberId(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm">
            <option value="">{t('staffing.selectPerson')}</option>
            {available.map(m => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </select>
          <p className="text-xs text-gray-400 mt-1">{t('staffing.replaceCapHint')}</p>
        </div>

        <div className="flex gap-3 pt-1">
          <button onClick={submit} disabled={busy || !memberId}
            className="flex-1 bg-gray-900 text-white py-2 rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-40">
            {busy ? t('staffing.saving', '…') : t('staffing.replaceConfirm')}
          </button>
          <button onClick={onClose}
            className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg text-sm font-medium hover:bg-gray-200">
            {t('common:actions.cancel')}
          </button>
        </div>
      </div>
    </Modal>
  )
}
